/**
 * Algoritmo de repetición espaciada SM-2 (SuperMemo 2) para tarjetas de repaso.
 *
 * Implementación estándar y pura (sin I/O): dada una tarjeta y la calidad
 * con la que el usuario la contestó (0–5), calcula el próximo intervalo,
 * el factor de facilidad y la fecha del siguiente repaso.
 *
 * Referencia: https://www.supermemo.com/en/blog/application-of-a-computer-to-improve-the-results-obtained-in-working-with-the-supermemo-method
 *
 * Módulo puro → testeable directamente.
 */

import type { ReviewCard } from '@/types/domain'

/** Grado de calidad con el que el usuario contestó una tarjeta. */
export type Quality = 'again' | 'hard' | 'good' | 'easy'

/**
 * Mapeo de los 4 botones de la UI a la calidad numérica SM-2 (0–5).
 * SM-2 considera acierto a partir de 3; below 3 = lapsus (reinicia).
 *  - again (no la sabía): 1
 *  - hard (con dificultad): 2  → cuenta como lapsus en este ajuste práctico,
 *    para reforzar tarjetas difíciles más a menudo.
 *  - good (correcto, esfuerzo normal): 4
 *  - easy (correcto, sin esfuerzo): 5
 *
 * Nota: usamos 2 como "lapse" para hard porque la experiencia de Anki
 * muestra que los usuarios esperan que "hard" acorte mucho el intervalo.
 */
export const QUALITY_GRADE: Record<Quality, number> = {
  again: 1,
  hard: 2,
  good: 4,
  easy: 5,
}

const MIN_EASE = 1.3

/** Subset de ReviewCard que SM-2 actualiza. */
export type SrsUpdate = Pick<
  ReviewCard,
  'intervalDays' | 'repetitions' | 'easeFactor' | 'lapses' | 'dueDate'
>

/**
 * Aplica SM-2 a una tarjeta dada su calidad (0–5) y devuelve los campos
 * actualizados. No muta la entrada.
 *
 * @param card tarjeta actual.
 * @param quality numérico 0–5 (ver `QUALITY_GRADE`).
 * @param todayKey fecha de hoy en formato `YYYY-MM-DD` (para calcular
 *                 `dueDate`); por defecto la de hoy.
 */
export function sm2(
  card: Pick<ReviewCard, 'intervalDays' | 'repetitions' | 'easeFactor' | 'lapses'>,
  quality: number,
  todayKey: string = today(),
): SrsUpdate {
  let { intervalDays, repetitions, easeFactor, lapses } = card

  if (quality < 3) {
    // Lapsus: reiniciar el aprendizaje.
    repetitions = 0
    intervalDays = 1
    lapses = lapses + 1
  } else {
    // Acierto: avanzar según el esquema SM-2.
    repetitions = repetitions + 1
    if (repetitions === 1) {
      intervalDays = 1
    } else if (repetitions === 2) {
      intervalDays = 6
    } else {
      intervalDays = Math.round(intervalDays * easeFactor)
    }
  }

  // Actualizar factor de facilidad (solo si hubo respuesta; la fórmula
  // clásica de SM-2). Clamp a 1.3.
  easeFactor = easeFactor + (0.1 - (5 - quality) * (0.08 + (5 - quality) * 0.02))
  if (easeFactor < MIN_EASE) easeFactor = MIN_EASE

  return {
    intervalDays,
    repetitions,
    easeFactor,
    lapses,
    dueDate: addDays(todayKey, intervalDays),
  }
}

/** ¿La tarjeta está vencida respecto a `todayKey`? */
export function isDue(card: Pick<ReviewCard, 'dueDate'>, todayKey: string = today()): boolean {
  return card.dueDate <= todayKey
}

/** Devuelve la fecha de hoy como `YYYY-MM-DD` (local). */
export function today(): string {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/**
 * Suma `n` días a una fecha `YYYY-MM-DD` y devuelve otra fecha `YYYY-MM-DD`.
 * Trabaja en zona local para coincidir con `today()`.
 */
export function addDays(isoDate: string, n: number): string {
  const [y, m, d] = isoDate.split('-').map(Number)
  const date = new Date(y, (m ?? 1) - 1, d ?? 1)
  date.setDate(date.getDate() + n)
  const ny = date.getFullYear()
  const nm = String(date.getMonth() + 1).padStart(2, '0')
  const nd = String(date.getDate()).padStart(2, '0')
  return `${ny}-${nm}-${nd}`
}
